import React from 'react';
import classnames from 'classnames';

import {useOverlayTriggerState} from '@react-stately/overlays';
import type {Node, Selection} from '@react-types/shared';
import type {TabListState} from '@react-stately/tabs';
import {mergeProps} from '@react-aria/utils';
import {useButton} from '@react-aria/button';

import type {AriaButtonProps} from '@react-types/button';

import {TypographySize, useTypography} from '@time-webkit/all/hooks/typography';

import {PopoverOverlay} from '../../../../molecules/popover';
import {ListBox, ListBoxItem} from '../../../../atoms/listbox';
import {CheckIcon} from '../../../../icons/check';
import {Arrow} from '../../../../icons/arrow';

import styles from '../styles.module.css';

export interface ShowMoreProps {
    state: TabListState<Record<string, string>>;
    moreLabel: string;
    count: number;
}

export type ShowMoreButtonProps = {
    btnRef: React.RefObject<HTMLButtonElement>;
    hasSelected?: boolean;
} & AriaButtonProps<'button'>;

const ShowMoreButton = (props: ShowMoreButtonProps) => {
    const {buttonProps} = useButton(props, props.btnRef);
    const tabTypography = useTypography({size: TypographySize.BodyM});

    return (
        <button
            {...mergeProps(props, buttonProps)}
            ref={props.btnRef}
            className={classnames(tabTypography, styles.row_tab_button, styles.show_more_btn, {
                [styles.active]: props.hasSelected,
            })}
        >
            {props.children}
        </button>
    );
};

const DEFAULT_OVERLAY_STATE = {};

export const ShowMore: React.FC<ShowMoreProps> = ({state, count, moreLabel}) => {
    const slicedCollection = Array.from(state.collection).slice(-count);
    const popoverState = useOverlayTriggerState(DEFAULT_OVERLAY_STATE);

    const handleSelect = (keys: Selection) => {
        const selected = Array.from(keys);

        if (selected[0]) {
            state.setSelectedKey(selected[0]);
        }
    };

    const hasSelected = slicedCollection.some((item) => state.selectedKey === item.key);

    return (
        <PopoverOverlay
            state={popoverState}
            placement='bottom right'
            renderTriggerElement={(triggerProps, ref: React.RefObject<HTMLButtonElement>, triggerState) => {
                return (
                    <ShowMoreButton
                        {...triggerProps}
                        btnRef={ref}
                        hasSelected={hasSelected}
                    >
                        <span>{`${moreLabel} ${count}`}</span>
                        <Arrow
                            width={16}
                            height={16}
                            className={classnames(styles.arrow, triggerState.isOpen ? styles.arrow_up : styles.arrow_down)}
                        />
                    </ShowMoreButton>
                );
            }}
        >
            <ListBox
                items={slicedCollection}
                className={styles.dropdown}
                onSelectionChange={handleSelect}
                selectionMode='single'
                shouldFocusWrap={true}
            >
                {(item: Node<Record<string, string>>) => {
                    const isSelected = state.selectedItem.key === item.key;

                    return (
                        <ListBoxItem {...item}>
                            <div className={styles.list_tab_button} onClick={() => popoverState.setOpen(false)}>
                                <span>{item.rendered}</span>
                                {isSelected && <CheckIcon size={16} className={styles.check} />}
                            </div>
                        </ListBoxItem>
                    );
                }}
            </ListBox>
        </PopoverOverlay>
    );
};
